import React, { memo, useState, useEffect } from 'react';
import { MarkdownContent } from './MarkdownRenderer.styles.js';
import { DEFAULT_LOCALE } from '../constants/locale.js';
import { DEFAULT_THEME } from '../constants/theme.js';
import ThemeContextProvider from '../contexts/ThemeContextProvider.js';
import { generateHtml } from '../utils/markdown.utils.js';

// Define default prop values outside of component to resolve rerender errors
// See https://stackoverflow.com/questions/71060000/what-is-the-status-of-the-default-props-rerender-trap-in-react
const DEFAULT_MARKDOWN = '';
const DEFAULT_ONTOCUPDATE = () => null;
const DEFAULT_PADDING = true;

/**
 * React Component for rendering markdown.
 *
 * @note Uses `dangerouslySetInnerHTML` to render the markdown content.
 *
 * @param {Object} props
 * @param {string} [props.markdown=''] The markdown that you want to render.
 * @param {string} [props.locale='en-US'] The language that markdown is in. This will help the smart quotes look right. Default is set to en-US.
 * @param {callback} [props.onTocUpdate] Callback function to update table of content
 * @param {boolean} [props.padding=true] Render markdown content container with or without padding.
 * @param {string} [props.theme='light'] Markdown color theme. Controls the markdown color styles. Default is set to 'light'.
 * @throws {Error} When the markdown contains forbidden HTML
 */
const MarkdownRenderer = _ref => {
  let {
    markdown = DEFAULT_MARKDOWN,
    locale = DEFAULT_LOCALE,
    onTocUpdate = DEFAULT_ONTOCUPDATE,
    padding = DEFAULT_PADDING,
    theme = DEFAULT_THEME
  } = _ref;
  const [sanitizedHtml, setSanitizedHtml] = useState();
  useEffect(() => {
    if (!markdown) return;
    setSanitizedHtml(generateHtml(markdown, locale, onTocUpdate, theme));
  }, [markdown, locale, onTocUpdate, theme]);
  if (!sanitizedHtml) return null;
  return /*#__PURE__*/React.createElement(ThemeContextProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(MarkdownContent, {
    $padding: padding,
    dangerouslySetInnerHTML: {
      __html: sanitizedHtml
    }
  }));
};
// Prevent the component from re-rendering if the props have not changed.
var MarkdownRenderer$1 = /*#__PURE__*/memo(MarkdownRenderer);

export { MarkdownRenderer$1 as default };
